import * as z from "zod";
import { Expr } from "../expr/ast";
import { JsonValue } from "../json";

export const AUTOMATION_SCHEMA_VERSION = 2;
export const TRIGGER_NODE_ID = "trigger";
export const DEFAULT_PORT = "next";
/**
 * Every step with side effects has an implicit error port. Connected, a
 * failure routes there (the error is handled, like matching on Err);
 * unconnected, a failure ends the run (like `?` propagating to the top).
 */
export const ERROR_PORT = "error";

export interface NodeUi {
  x: number;
  y: number;
}

export interface TriggerNode {
  id: typeof TRIGGER_NODE_ID;
  kind: string;
  kindVersion: number;
  config: JsonValue;
  description?: string;
  ui?: NodeUi;
}

export interface StepNode {
  id: string;
  kind: string;
  kindVersion: number;
  config: JsonValue;
  title?: string;
  description?: string;
  /** Skipped at runtime: the run follows the step's default port. */
  disabled?: boolean;
  ui?: NodeUi;
}

/** One edge per (from, port); a node may have many incoming edges (paths can merge). */
export interface Edge {
  from: string;
  port: string;
  to: string;
}

export interface AutomationPolicies {
  /** Evaluated before a run is admitted; false means the run never starts. */
  entryCondition?: Expr;
}

export type AutomationStatus = "draft" | "active" | "inactive";

export interface AutomationDefinition {
  schemaVersion: typeof AUTOMATION_SCHEMA_VERSION;
  id: string;
  name: string;
  /** Monotonic per automation. Runs pin the version they started on. */
  version: number;
  status: AutomationStatus;
  trigger: TriggerNode;
  nodes: StepNode[];
  edges: Edge[];
  policies: AutomationPolicies;
}

const exprSchema: z.ZodType<Expr> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("literal"), value: jsonSchema }),
    z.object({ kind: z.literal("ref"), path: pathSchema }),
    z.object({ kind: z.literal("var"), name: z.string(), path: pathSchema }),
    z.object({
      kind: z.literal("call"),
      fn: z.string(),
      args: z.array(exprSchema),
    }),
    z.object({
      kind: z.literal("lambda"),
      param: z.string(),
      body: exprSchema,
    }),
  ]),
);

export const jsonSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonSchema),
    z.record(z.string(), jsonSchema),
  ]),
);

const pathSchema = z.array(
  z.object({
    field: z.string(),
    args: z.record(z.string(), jsonSchema).optional(),
  }),
);

export const expressionSchema = exprSchema;

const nodeUiSchema = z.object({ x: z.number(), y: z.number() });

export const automationDefinitionSchema = z.object({
  schemaVersion: z.literal(AUTOMATION_SCHEMA_VERSION),
  id: z.string(),
  name: z.string(),
  version: z.number().int().nonnegative(),
  // Unrelated to bill_of_materials_status, which shares these values. //redontIgnore
  status: z.enum(["draft", "active", "inactive"]),
  trigger: z.object({
    id: z.literal(TRIGGER_NODE_ID),
    kind: z.string(),
    kindVersion: z.number().int(),
    config: jsonSchema,
    description: z.string().optional(),
    ui: nodeUiSchema.optional(),
  }),
  nodes: z.array(
    z.object({
      id: z.string().min(1),
      kind: z.string(),
      kindVersion: z.number().int(),
      config: jsonSchema,
      title: z.string().optional(),
      description: z.string().optional(),
      disabled: z.boolean().optional(),
      ui: nodeUiSchema.optional(),
    }),
  ),
  edges: z.array(
    z.object({ from: z.string(), port: z.string(), to: z.string() }),
  ),
  policies: z.object({ entryCondition: exprSchema.optional() }),
});

export function getNode(
  automation: AutomationDefinition,
  id: string,
): StepNode | undefined {
  return automation.nodes.find((n) => n.id === id);
}

export function outgoing(
  automation: AutomationDefinition,
  nodeId: string,
): Edge[] {
  return automation.edges.filter((e) => e.from === nodeId);
}

export function nextNodeId(
  automation: AutomationDefinition,
  nodeId: string,
  port: string,
): string | undefined {
  return automation.edges.find((e) => e.from === nodeId && e.port === port)?.to;
}

/** GraphQL-safe field name under `steps` for a node's outputs (GraphQL reserves names starting with `__`). */
export function stepFieldName(nodeId: string): string {
  const cleaned = nodeId.replace(/[^_0-9A-Za-z]/g, "_");
  return /^([A-Za-z]|_(?!_))/.test(cleaned) ? cleaned : `s_${cleaned}`;
}
